import React, { useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import {
  useBillingReconciliationLifecycleAlerts,
  useUpdateBillingReconciliationLifecycleAlert,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { billingErrorMessage } from './errorMessage.js';

export const severityColor = (severity) => {
  if (severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
};

export const severityRank = (severity) => ({ error: 0, warning: 1, info: 2 }[severity] ?? 3);

const ACRONYMS = new Set(['BAC', 'CPQ', 'DPE', 'GM', 'PPC']);

const titleFromCode = (value) => String(value || 'Lifecycle alert')
  .replace(/_/g, ' ')
  .toLowerCase()
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .split(' ')
  .map((part) => (ACRONYMS.has(part.toUpperCase()) ? part.toUpperCase() : part))
  .join(' ');

export const lifecycleAlertLabel = (code) => ({
  ACTION_REQUIRED_DEALER: 'Action Required Alert',
  IN_PROGRESS_WITH_CPQ: 'In Progress - CPQ',
  IN_PROGRESS_WITH_APPSHEET: 'In Progress - AppSheet',
  PACKAGE_ANCHOR_MISSING: 'Missing Package Anchor',
  PACKAGE_EXPIRING_SOON: 'Package Expiring Soon',
  COOLING_PERIOD: 'Cooling Period',
  COOLING_STILL_BILLING: 'Cooling Still Billing',
  PENDING_PROVIDER_CHANGE_ACTIVE: 'Pending Provider Change',
  PPC_STILL_BILLING: 'PPC Still Billing',
}[code] || titleFromCode(code));

export const alertDealerName = (alert = {}) => (
  alert.details?.dealerName
  || alert.details?.dealer
  || alert.details?.dealerDisplayName
  || alert.dealerName
  || alert.dealerDisplayName
  || ''
);

const bacDealerLine = (alert) => {
  const dealerName = alertDealerName(alert);
  return `BAC ${alert.bac || '--'}${dealerName ? ` - ${dealerName}` : ''}`;
};

const groupLifecycleAlerts = (alerts = []) => {
  const grouped = new Map();
  [...alerts]
    .sort((left, right) => (
      severityRank(left.severity) - severityRank(right.severity)
      || String(left.bac || '').localeCompare(String(right.bac || ''), undefined, { numeric: true })
      || String(left.details?.dealerName || '').localeCompare(String(right.details?.dealerName || ''))
    ))
    .forEach((alert) => {
      const code = alert.code || 'Lifecycle alert';
      if (!grouped.has(code)) grouped.set(code, []);
      grouped.get(code).push(alert);
    });
  return [...grouped.entries()].sort((left, right) => (
    severityRank(left[1][0]?.severity) - severityRank(right[1][0]?.severity)
    || right[1].length - left[1].length
    || String(left[0]).localeCompare(String(right[0]))
  ));
};

const LifecycleAlertsPanel = ({ runId, limit = 500, onOpenBac = null }) => {
  const query = useBillingReconciliationLifecycleAlerts({ runId, dismissed: false, limit });
  const dismiss = useUpdateBillingReconciliationLifecycleAlert();
  const [pendingDismiss, setPendingDismiss] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissError, setDismissError] = useState('');
  const alerts = useMemo(() => query.data || [], [query.data]);
  const groupedAlerts = useMemo(() => groupLifecycleAlerts(alerts), [alerts]);
  const hasAlerts = alerts.length > 0;
  const openAlertContext = (alert) => onOpenBac?.(alert.bac, {
    source: 'lifecycle',
    alertId: alert.id,
    title: `${lifecycleAlertLabel(alert.code)} - BAC ${alert.bac}`,
    code: alert.code,
    severity: alert.severity || 'info',
    dealerName: alertDealerName(alert),
    summary: alert.details?.summary || '',
    detail: 'Lifecycle alert source context',
  });
  const requestDismiss = (alert) => {
    dismiss.reset?.();
    setDismissError('');
    setPendingDismiss(alert);
    setDismissReason('');
  };
  const requestDismissGroup = (code, rows) => {
    dismiss.reset?.();
    setDismissError('');
    setPendingDismiss({ id: `group:${code}`, code, rows, severity: rows[0]?.severity || 'info', isGroup: true });
    setDismissReason('');
  };
  const closeDismissDialog = () => {
    dismiss.reset?.();
    setDismissError('');
    setPendingDismiss(null);
    setDismissReason('');
  };
  const confirmDismiss = async () => {
    const reason = dismissReason.trim();
    if (!pendingDismiss || !reason) return;
    setDismissError('');
    if (pendingDismiss.isGroup) {
      try {
        for (const alert of pendingDismiss.rows || []) {
          await dismiss.mutateAsync({
            id: alert.id,
            patch: { dismissed: true, dismissed_reason: reason },
          });
        }
        closeDismissDialog();
      } catch (error) {
        setDismissError(billingErrorMessage(error, 'Lifecycle alerts could not be dismissed.'));
      }
      return;
    }
    dismiss.mutate(
      { id: pendingDismiss.id, patch: { dismissed: true, dismissed_reason: reason } },
      {
        onSuccess: closeDismissDialog,
        onError: (error) => setDismissError(billingErrorMessage(error, 'Lifecycle alert could not be dismissed.')),
      },
    );
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <NotificationsActiveOutlinedIcon color={hasAlerts ? 'warning' : 'success'} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Lifecycle Alerts
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Enrollment states and lifecycle gaps that need billing attention
            </Typography>
          </Box>
          <Chip
            size="small"
            color={hasAlerts ? 'warning' : 'success'}
            variant={hasAlerts ? 'filled' : 'outlined'}
            label={`${alerts.length} active`}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        {query.isLoading ? (
          <Stack spacing={1}>
            <Skeleton height={42} />
            <Skeleton height={42} />
          </Stack>
        ) : query.isError ? (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => query.refetch()}>Retry</Button>}
          >
            Lifecycle alerts could not be loaded.
          </Alert>
        ) : hasAlerts ? (
          <Stack spacing={1}>
            {groupedAlerts.map(([code, rows]) => {
              const errors = rows.filter((alert) => alert.severity === 'error').length;
              const warnings = rows.filter((alert) => alert.severity === 'warning').length;
              const severity = errors ? 'error' : warnings ? 'warning' : 'info';
              const label = lifecycleAlertLabel(code);
              return (
              <Accordion key={code} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {label}
                    </Typography>
                    <Chip size="small" color={severityColor(severity)} variant="outlined" label={rows.length} />
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label={`Dismiss all ${label} alerts`}
                      disabled={dismiss.isPending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestDismissGroup(code, rows);
                      }}
                      sx={{ ml: 'auto', textTransform: 'none' }}
                    >
                      Dismiss all
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1}>
            {rows.map((alert) => (
              <Stack
                key={alert.id}
                role={onOpenBac ? 'button' : undefined}
                tabIndex={onOpenBac ? 0 : undefined}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                onClick={() => openAlertContext(alert)}
                onKeyDown={(event) => {
                  if (!onOpenBac || (event.key !== 'Enter' && event.key !== ' ')) return;
                  event.preventDefault();
                  openAlertContext(alert);
                }}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) => (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.035)'
                    : 'rgba(15, 23, 42, 0.025)'),
                  cursor: onOpenBac ? 'pointer' : 'default',
                  '&:hover': onOpenBac ? {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => (theme.palette.mode === 'dark'
                      ? 'rgba(144, 202, 249, 0.10)'
                      : 'rgba(25, 118, 210, 0.06)'),
                  } : undefined,
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" color={severityColor(alert.severity)} label={alert.severity || 'info'} />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {lifecycleAlertLabel(alert.code)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {bacDealerLine(alert)}
                  </Typography>
                  {alert.details?.summary && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {alert.details.summary}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    openAlertContext(alert);
                  }}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, textTransform: 'none' }}
                >
                  Details
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(event) => {
                    event.stopPropagation();
                    requestDismiss(alert);
                  }}
                  disabled={dismiss.isPending}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, textTransform: 'none' }}
                >
                  Dismiss
                </Button>
              </Stack>
            ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Stack>
        ) : null}
        </Stack>
      </Paper>
      <Dialog open={Boolean(pendingDismiss)} onClose={closeDismissDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{pendingDismiss?.isGroup ? `Dismiss ${lifecycleAlertLabel(pendingDismiss?.code)} alerts` : 'Dismiss lifecycle alert'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity={severityColor(pendingDismiss?.severity || 'info')}>
              {pendingDismiss?.isGroup
                ? `${pendingDismiss.rows?.length || 0} alerts in this group will be dismissed.`
                : 'Review the BAC details before dismissing this alert.'}
            </Alert>
            <TextField
              autoFocus
              label="Dismissal reason"
              value={dismissReason}
            onChange={(event) => setDismissReason(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Dismissal reason' }}
          />
          {dismissError && (
            <Alert severity="error" onClose={() => setDismissError('')}>
              {dismissError}
            </Alert>
          )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDismissDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmDismiss}
            disabled={!dismissReason.trim() || dismiss.isPending}
          >
            Dismiss alert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LifecycleAlertsPanel;
